import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  NavigationArrow, MapPin, CheckCircle, Clock, WifiSlash, ArrowsClockwise,
  Star, Heart, Spinner,
} from "@phosphor-icons/react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30_000;

const STEPS = [
  { key: "assigned",  label: "Booked",      icon: Clock,            desc: "Visit confirmed and scheduled" },
  { key: "en_route",  label: "On the way",  icon: NavigationArrow,  desc: "Your supplier is heading over" },
  { key: "on_site",   label: "On site",     icon: MapPin,           desc: "They have arrived at the property" },
  { key: "completed", label: "Completed",   icon: CheckCircle,      desc: "Work has been completed" },
];

const STATUS_INDEX = { assigned: 0, en_route: 1, on_site: 2, completed: 3 };

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function useCountdown(etaAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!etaAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [etaAt]);
  if (!etaAt) return null;
  const diff = new Date(etaAt).getTime() - now;
  if (!Number.isFinite(diff)) return null;
  if (diff <= 0) return "Due any moment";
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

export default function TrackerPage() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [mapTick, setMapTick] = useState(Date.now());
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API_ROOT}/public/tracker/${token}`);
      if (!mountedRef.current) return;
      setData(r.data);
      setError(null);
      setLastRefreshed(new Date());
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.response?.status === 404 ? "not_found" : "load_error");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    const id = setInterval(fetchData, REFRESH_MS);
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const onVis = () => { if (!document.hidden) fetchData(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchData]);

  // Gently refresh the map image every few minutes so it stays "live"
  useEffect(() => {
    const t = setInterval(() => setMapTick(Date.now()), 180_000);
    return () => clearInterval(t);
  }, []);

  // Apply tenant primary colour as a CSS variable
  useEffect(() => {
    if (data?.branding?.primary_color) {
      document.documentElement.style.setProperty("--tracker-accent", data.branding.primary_color);
    }
    return () => {
      document.documentElement.style.removeProperty("--tracker-accent");
    };
  }, [data?.branding?.primary_color]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="text-sm text-slate-400 flex items-center gap-2" data-testid="tracker-loading">
          <ArrowsClockwise size={16} className="animate-spin" /> Loading tracker…
        </div>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6" data-testid="tracker-not-found">
        <div className="max-w-sm text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-400">
            <WifiSlash size={28} />
          </div>
          <h1 className="text-lg font-semibold">Tracker unavailable</h1>
          <p className="text-sm text-slate-400">This tracking link has expired or is invalid. Please contact your service provider for an updated link.</p>
        </div>
      </div>
    );
  }

  if (error === "load_error" || !data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-3" data-testid="tracker-error">
          <h1 className="text-lg font-semibold">Couldn't load tracker</h1>
          <p className="text-sm text-slate-400">Please check your connection and try again.</p>
          <button onClick={fetchData} className="mt-2 text-xs px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700" data-testid="tracker-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_INDEX[data.status] ?? 0;
  const countdown = data.status === "en_route" ? <Countdown etaAt={data.eta_at} /> : null;
  const brandColor = data?.branding?.primary_color || "#1D4ED8";
  const companyName = data?.branding?.company_name || "Your Service Provider";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="tracker-page">
      {/* Header */}
      <header className="border-b border-slate-800/80 bg-slate-900/60 backdrop-blur">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-3">
          {data?.branding?.logo_url ? (
            <img
              src={data.branding.logo_url}
              alt={companyName}
              className="h-9 w-9 object-contain rounded bg-white/5 p-1"
              data-testid="tracker-brand-logo"
            />
          ) : (
            <div
              className="h-9 w-9 rounded flex items-center justify-center font-semibold text-white text-sm"
              style={{ backgroundColor: brandColor }}
            >
              {companyName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Live Tracker</p>
            <p className="text-sm font-semibold truncate" data-testid="tracker-company-name">{companyName}</p>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 py-6 space-y-6 pb-24">
        {/* Hero status card */}
        <section
          className="rounded-2xl p-5 shadow-lg relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${brandColor}22 0%, ${brandColor}08 100%)`, border: `1px solid ${brandColor}33` }}
          data-testid="tracker-hero"
        >
          <p className="text-[11px] uppercase tracking-widest text-slate-400">Status</p>
          <h1 className="text-2xl font-bold mt-1" style={{ color: brandColor }} data-testid="tracker-status-label">
            {STEPS[currentIdx].label}
          </h1>
          <p className="text-sm text-slate-300 mt-1">{STEPS[currentIdx].desc}</p>

          {countdown && (
            <div className="mt-4 inline-flex items-baseline gap-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800" data-testid="tracker-countdown">
              <NavigationArrow size={14} weight="fill" style={{ color: brandColor }} />
              <span className="text-xs text-slate-400">ETA</span>
              <span className="text-base font-mono font-semibold">{countdown}</span>
            </div>
          )}

          {data.status === "on_site" && data.arrived_at && (
            <p className="mt-3 text-xs text-emerald-400">Arrived at {formatTime(data.arrived_at)}</p>
          )}
          {data.status === "completed" && data.completed_at && (
            <p className="mt-3 text-xs text-emerald-400">Completed at {formatTime(data.completed_at)}</p>
          )}
        </section>

        {/* Supplier card */}
        {(data.supplier_name || data.supplier_company) && (
          <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="tracker-supplier-card">
            <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Your supplier</p>
            <div className="flex items-center gap-3">
              <div
                className="h-11 w-11 rounded-full flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: brandColor }}
              >
                {(data.supplier_name || data.supplier_company || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                {data.supplier_company && <p className="text-sm font-semibold truncate">{data.supplier_company}</p>}
                {data.supplier_name && <p className="text-xs text-slate-400 truncate">{data.supplier_name}</p>}
                {data.supplier_trade && <p className="text-[11px] text-slate-500 mt-0.5">{data.supplier_trade}</p>}
              </div>
            </div>
          </section>
        )}

        {/* Map */}
        {data.has_map && (
          <section className="rounded-xl overflow-hidden border border-slate-800 bg-slate-900/40" data-testid="tracker-map">
            <img
              src={`${API_ROOT}/public/tracker/${token}/map.png?width=800&height=400&zoom=15&t=${mapTick}`}
              alt="Job location"
              className="w-full aspect-[2/1] object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            {data.property_label && (
              <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-300">
                <MapPin size={14} style={{ color: brandColor }} />
                <span className="truncate">{data.property_label}</span>
              </div>
            )}
          </section>
        )}

        {/* Timeline */}
        <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid="tracker-timeline">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-4">Progress</p>
          <ol className="relative space-y-5 pl-8">
            <span className="absolute left-[14px] top-1 bottom-1 w-px bg-slate-800" aria-hidden />
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const reached = i <= currentIdx;
              const active = i === currentIdx;
              const time =
                s.key === "en_route" ? formatTime(data.en_route_at) :
                s.key === "on_site"  ? formatTime(data.arrived_at)  :
                s.key === "completed"? formatTime(data.completed_at):
                "";
              return (
                <li key={s.key} className="relative" data-testid={`tracker-step-${s.key}`}>
                  <span
                    className={`absolute -left-8 top-0 flex items-center justify-center h-7 w-7 rounded-full text-white transition ${
                      active ? "ring-4 ring-offset-0" : ""
                    }`}
                    style={{
                      backgroundColor: reached ? brandColor : "#1e293b",
                      boxShadow: active ? `0 0 0 4px ${brandColor}33` : "none",
                    }}
                  >
                    <Icon size={14} weight={reached ? "fill" : "regular"} />
                  </span>
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm font-semibold ${reached ? "text-slate-100" : "text-slate-500"}`}>{s.label}</p>
                    {time && <span className="text-xs text-slate-400 font-mono">{time}</span>}
                  </div>
                  <p className={`text-xs mt-0.5 ${reached ? "text-slate-400" : "text-slate-600"}`}>{s.desc}</p>
                </li>
              );
            })}
          </ol>
        </section>

        {/* Rating + Tip — shown only on Completed */}
        {data.status === "completed" && (
          <RatingTipCard
            token={token}
            data={data}
            brandColor={brandColor}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            onRated={fetchData}
          />
        )}

        {/* Footer */}
        <footer className="flex items-center justify-between text-[11px] text-slate-500 px-1">
          <span className="flex items-center gap-1.5">
            {offline
              ? <><WifiSlash size={12} className="text-rose-400" /> Offline</>
              : <><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live</>}
            {lastRefreshed && ` · Updated ${formatTime(lastRefreshed.toISOString())}`}
          </span>
          <button
            onClick={fetchData}
            className="hover:text-slate-300 inline-flex items-center gap-1"
            data-testid="tracker-refresh-btn"
          >
            <ArrowsClockwise size={11} /> Refresh
          </button>
        </footer>
      </main>
    </div>
  );
}

function Countdown({ etaAt }) {
  const c = useCountdown(etaAt);
  return <>{c || "—"}</>;
}

function RatingTipCard({ token, data, brandColor, searchParams, setSearchParams, onRated }) {
  const [stars, setStars] = useState(data.rating || 0);
  const [hoverStars, setHoverStars] = useState(0);
  const [comment, setComment] = useState(data.rating_comment || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!data.rating);

  // Tip state
  const [tipAmount, setTipAmount] = useState(10);
  const [customTip, setCustomTip] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [tipLoading, setTipLoading] = useState(false);
  const [tipPollMsg, setTipPollMsg] = useState(null);
  const [tipPaidLocal, setTipPaidLocal] = useState(data.tip_paid || false);
  const [tipPaidAmount, setTipPaidAmount] = useState(data.tip_amount || 0);

  // Poll tip status if we came back from checkout
  useEffect(() => {
    const sid = searchParams.get("tip_session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    setTipPollMsg("Verifying your tip…");
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API_ROOT}/public/tracker/${token}/tip/status/${sid}`);
        if (cancelled) return;
        if (r.data?.payment_status === "paid") {
          setTipPaidLocal(true);
          setTipPaidAmount(r.data?.amount || tipAmount);
          setTipPollMsg(null);
          const next = new URLSearchParams(searchParams);
          next.delete("tip_session_id");
          setSearchParams(next, { replace: true });
          onRated?.();
          return;
        }
        if (["expired", "failed", "cancelled"].includes(r.data?.payment_status)) {
          setTipPollMsg("Payment was not completed. Feel free to try again.");
          return;
        }
      } catch { /* ignore and retry */ }
      if (attempts < maxAttempts && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setTipPollMsg("Still processing — we'll update you shortly.");
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (searchParams.get("tip_cancelled")) {
      setTipPollMsg("Tip cancelled. No charge was made.");
      const next = new URLSearchParams(searchParams);
      next.delete("tip_cancelled");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const submitRating = async () => {
    if (stars < 1) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_ROOT}/public/tracker/${token}/rating`, { stars, comment });
      setSubmitted(true);
      onRated?.();
    } catch { /* silent; user can retry */ }
    finally { setSubmitting(false); }
  };

  const launchTip = async () => {
    const amount = useCustom ? parseFloat(customTip) : tipAmount;
    if (!Number.isFinite(amount) || amount < 1) return;
    setTipLoading(true);
    try {
      const r = await axios.post(`${API_ROOT}/public/tracker/${token}/tip`, {
        amount,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch {
      setTipPollMsg("Couldn't start checkout. Please try again.");
    } finally {
      setTipLoading(false);
    }
  };

  const shown = Math.max(stars, hoverStars);

  return (
    <section
      className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 space-y-5"
      data-testid="tracker-rating-card"
    >
      <div>
        <p className="text-[11px] uppercase tracking-widest text-slate-400">How did we do?</p>
        <h3 className="text-base font-semibold mt-0.5">
          {submitted ? "Thanks for the feedback" : "Rate your visit"}
        </h3>
      </div>

      {/* Stars */}
      <div className="flex items-center gap-1.5" data-testid="tracker-rating-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={submitting || submitted}
            onClick={() => setStars(n)}
            onMouseEnter={() => !submitted && setHoverStars(n)}
            onMouseLeave={() => !submitted && setHoverStars(0)}
            className="p-1 transition transform hover:scale-110 disabled:cursor-default"
            style={{ color: n <= shown ? brandColor : "#334155" }}
            data-testid={`tracker-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            <Star size={32} weight={n <= shown ? "fill" : "regular"} />
          </button>
        ))}
      </div>

      {/* Comment (shown once a star is tapped) */}
      {stars > 0 && !submitted && (
        <div className="space-y-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 1000))}
            placeholder="Anything else you'd like to share? (optional)"
            rows={2}
            className="w-full bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:border-slate-600"
            data-testid="tracker-rating-comment"
          />
          <button
            type="button"
            onClick={submitRating}
            disabled={submitting || stars < 1}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-60"
            style={{ backgroundColor: brandColor }}
            data-testid="tracker-rating-submit"
          >
            {submitting ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} weight="fill" />}
            Submit rating
          </button>
        </div>
      )}

      {submitted && (
        <p className="text-sm text-slate-300" data-testid="tracker-rating-thanks">
          Your {stars}-star rating has been shared with {data?.branding?.company_name || "the team"}. Much appreciated!
        </p>
      )}

      {/* Tip */}
      {data.tips_enabled && (
        <div className="border-t border-slate-800 pt-5">
          {tipPaidLocal ? (
            <div
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm"
              style={{ backgroundColor: `${brandColor}22`, color: brandColor }}
              data-testid="tracker-tip-paid"
            >
              <Heart size={16} weight="fill" />
              Tip of £{Number(tipPaidAmount).toFixed(2)} received — thank you!
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Heart size={16} weight="fill" style={{ color: brandColor }} />
                <p className="text-sm font-semibold">Leave a tip</p>
                <span className="text-[11px] text-slate-500">Optional</span>
              </div>
              <div className="grid grid-cols-4 gap-2" data-testid="tracker-tip-presets">
                {[5, 10, 20].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { setUseCustom(false); setTipAmount(n); }}
                    className={`rounded-lg py-2 text-sm font-semibold border transition ${
                      !useCustom && tipAmount === n
                        ? "text-white"
                        : "text-slate-200 border-slate-800 hover:border-slate-600 bg-slate-950/60"
                    }`}
                    style={!useCustom && tipAmount === n ? { backgroundColor: brandColor, borderColor: brandColor } : {}}
                    data-testid={`tracker-tip-${n}`}
                  >
                    £{n}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setUseCustom(true)}
                  className={`rounded-lg py-2 text-sm font-semibold border transition ${
                    useCustom ? "text-white" : "text-slate-200 border-slate-800 hover:border-slate-600 bg-slate-950/60"
                  }`}
                  style={useCustom ? { backgroundColor: brandColor, borderColor: brandColor } : {}}
                  data-testid="tracker-tip-custom-toggle"
                >
                  Custom
                </button>
              </div>
              {useCustom && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-slate-400 text-sm">£</span>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    step="0.5"
                    value={customTip}
                    onChange={(e) => setCustomTip(e.target.value)}
                    placeholder="Amount"
                    className="flex-1 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:border-slate-600"
                    data-testid="tracker-tip-custom-input"
                  />
                </div>
              )}
              <button
                type="button"
                onClick={launchTip}
                disabled={tipLoading || (useCustom ? !(parseFloat(customTip) >= 1) : false)}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-60"
                style={{ backgroundColor: brandColor }}
                data-testid="tracker-tip-submit"
              >
                {tipLoading ? <Spinner size={14} className="animate-spin" /> : <Heart size={14} weight="fill" />}
                Tip {useCustom ? (customTip ? `£${customTip}` : "") : `£${tipAmount}`}
              </button>
              <p className="text-[11px] text-slate-500 mt-2">Secure payment via Stripe. 100% goes to the team.</p>
            </>
          )}
          {tipPollMsg && (
            <p className="mt-3 text-xs text-slate-400" data-testid="tracker-tip-message">{tipPollMsg}</p>
          )}
        </div>
      )}
    </section>
  );
}
